import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, Button } from '@mui/material';

/**
 * ErrorBoundary Component
 * 
 * A class component that catches JavaScript errors anywhere in its child component tree,
 * logs those errors, and displays a fallback UI instead of crashing the whole application.
 * 
 * This is particularly useful for handling runtime errors in production environments
 * where we want to provide a better user experience than a blank screen.
 * 
 * @extends {Component}
 */
class ErrorBoundary extends Component {
  /**
   * Initialize state to track error status
   */
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false,
      error: null,
      errorInfo: null 
    };
  }

  /**
   * Static lifecycle method that runs when an error is thrown in a child component
   * It's used to update the component's state with the error information
   * 
   * @param {Error} error - The error that was thrown
   * @returns {Object} Updated state object
   */
  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  /**
   * Lifecycle method that runs after an error has been thrown and caught
   * Use this to log the error to an error reporting service
   * 
   * @param {Error} error - The error that was thrown
   * @param {Object} errorInfo - Contains the component stack trace
   */
  componentDidCatch(error, errorInfo) {
    // Log error details to console in development
    console.error("Error caught by ErrorBoundary:", error);
    console.error("Component stack trace:", errorInfo.componentStack);
    
    // Save error details to state
    this.setState({ errorInfo });
    
    // Send error to a monitoring service if available
    this.logErrorToService(error, errorInfo);
  }

  /**
   * Log errors to an external monitoring service
   * Implement this when integrating with a service like Sentry
   * 
   * @param {Error} error - The error that was thrown
   * @param {Object} errorInfo - Contains the component stack trace
   */
  logErrorToService(error, errorInfo) {
    // This is a placeholder for actual error logging service implementation
    // In a production app, you would integrate with a service like Sentry:
    
    // if (typeof Sentry !== 'undefined') {
    //   Sentry.withScope((scope) => {
    //     scope.setExtras({ componentStack: errorInfo.componentStack });
    //     Sentry.captureException(error);
    //   });
    // }
  }

  /**
   * Reset the error boundary state
   * Useful when providing a "try again" functionality
   */
  resetErrorBoundary = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
    
    // Call the onReset callback if provided
    if (this.props.onReset) {
      this.props.onReset();
    }
  };

  /**
   * Render either the fallback UI or the children components
   * 
   * @returns {React.ReactNode} - The rendered component
   */
  render() {
    const { hasError, error, errorInfo } = this.state;
    const { fallback, children } = this.props;
    
    // If an error occurred, show the fallback UI
    if (hasError) {
      // If a custom fallback is provided, use it
      if (fallback) {
        return fallback(error, errorInfo, this.resetErrorBoundary);
      }
      
      // Default fallback UI
      return (
        <Box 
          sx={{ 
            p: 3, 
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            minHeight: '200px',
            border: '1px solid #f5c6cb',
            borderRadius: 1,
            bgcolor: '#f8d7da',
            my: 2,
            mx: 'auto',
            maxWidth: '600px',
          }}
        >
          <Typography variant="h5" color="error" gutterBottom fontWeight="500">
            Something went wrong
          </Typography>
          <Typography variant="body1" color="error.dark" paragraph>
            {error?.message || 'An unexpected error occurred'}
          </Typography>
          {process.env.NODE_ENV === 'development' && errorInfo && (
            <Box 
              sx={{ 
                my: 2, 
                p: 2, 
                bgcolor: 'rgba(0,0,0,0.05)', 
                borderRadius: 1, 
                maxHeight: '200px', 
                overflow: 'auto',
                width: '100%',
                textAlign: 'left',
              }}
            >
              <Typography variant="body2" component="pre" sx={{ fontSize: '0.75rem' }}>
                {errorInfo.componentStack}
              </Typography>
            </Box>
          )}
          <Button 
            variant="contained" 
            color="primary"
            onClick={this.resetErrorBoundary}
            sx={{ mt: 2 }}
          >
            Try again
          </Button>
        </Box>
      );
    }

    // If no error, render the children normally
    return children;
  }
}

// PropTypes for component documentation and type validation
ErrorBoundary.propTypes = {
  /** The components that this error boundary wraps */
  children: PropTypes.node.isRequired,
  
  /** Optional custom fallback component function */
  fallback: PropTypes.func,
  
  /** Callback function called when the error boundary resets */
  onReset: PropTypes.func
};

export default ErrorBoundary; 